import React, { useEffect, useState } from 'react'
import { Button, Card, Col, Collapse, Container, Row } from 'react-bootstrap'
import '../components/css/prooduct-details.css';
import { Minus, Plus } from 'lucide-react';
import { useDispatch, useSelector } from 'react-redux';
import { getRelatedActiveProducts, getSingleProducts } from '../App/Slices/productSlice';
import { useParams } from 'react-router-dom';
import RelatedProducts from '../components/OurProductPages/RelatedProducts';
import { toast } from 'react-toastify';
import { WhatsappShareButton } from 'react-share';
import buttonimg from '../components/images/Button .png'
import { Helmet } from 'react-helmet-async';

const ProductDetails = () => {
    const baseUrl = process.env.REACT_APP_BASE_URL;
    const { relatedproducts: products } = useSelector((state) => state.productsData);
    const { singleproducts } = useSelector((state) => state.productsData);
    const url = window.location.href;

    const dispatch = useDispatch();
    const [openSection, setOpenSection] = useState(null);
    const [images, setImages] = useState([]);
    const [selectedImage, setSelectedImage] = useState(null);
    const { slug } = useParams();

    // New states for zoom effect
    const [isZoomed, setIsZoomed] = useState(false);
    const [transformOrigin, setTransformOrigin] = useState('center center');

    const toggleSection = (index) => {
        setOpenSection(openSection === index ? null : index);
    };

    // Fetch product
    useEffect(() => {
        dispatch(getSingleProducts(slug));
    }, [dispatch, slug]);

    // Prepare images array (main + others)
    useEffect(() => {
        if (singleproducts) {
            let imgs = [];

            if (singleproducts?.image) {
                imgs.push(singleproducts.image);
            }

            if (Array.isArray(singleproducts?.other_images)) {
                imgs = [...imgs, ...singleproducts.other_images];
            } else if (typeof singleproducts?.other_images === "string") {
                try {
                    const parsed = JSON.parse(singleproducts?.other_images);
                    if (Array.isArray(parsed)) {
                        imgs = [...imgs, ...parsed];
                    }
                } catch (e) {
                    console.error("Invalid JSON in other images", e);
                }
            }

            setImages(imgs);
            setSelectedImage(singleproducts?.image || null);
        }
    }, [singleproducts]);

    // Fetch related products
    useEffect(() => {
        if (singleproducts?.category) {
            try {
                dispatch(
                    getRelatedActiveProducts({
                        category: singleproducts.category,
                        excludeId: singleproducts.id,
                    })
                );
            } catch (err) {
                console.error("Error fetching related products:", err.message);
                toast.error("Failed to fetch related products.");
            }
        }
    }, [dispatch, singleproducts]);

    // Handle mouse move for zoom effect
    const handleMouseMove = (e) => {
        const { left, top, width, height } = e.currentTarget.getBoundingClientRect();
        const x = ((e.clientX - left) / width) * 100;
        const y = ((e.clientY - top) / height) * 100;

        setTransformOrigin(`${x}% ${y}%`);
    };

    const handleMouseEnter = () => {
        setIsZoomed(true);
    };

    const handleMouseLeave = () => {
        setIsZoomed(false);
        setTransformOrigin('center center');
    };

    const sections = [
        { title: "INDICATIONS", description: singleproducts?.indications },
        { title: "COMPOSITION", description: singleproducts?.composition },
        { title: "DOSAGE", description: singleproducts?.dosages },
        { title: "PACK SIZE", description: singleproducts?.packsize },
        { title: "PHARMACEUTICAL FORM", description: singleproducts?.pharmacautionform },
    ];

    const shareHashtag = `#${singleproducts?.title}\n\n#${singleproducts?.title}\n\n#${singleproducts?.slug}\n\n#PanavBiotech`;
    const shareHashtag_fb = `#PanavBiotech #${singleproducts?.title}`;
    const shareMessage = `Hi, I found this product on Panav Biotech's website and I'm considering it for my pet.\n\n${singleproducts?.title}\n\n Here's the product link:\n\n`;

    const cleanHTML = (html) => {
        if (!html) return '';
        let cleaned = html.replace(/==\s*\$\d+/g, '');
        return cleaned;
    };

    return (
        <>
            <Helmet>
                <title>{singleproducts?.meta_title}</title>
                <meta name="description" content={singleproducts?.meta_description} />
                <meta property="og:title" content={singleproducts?.meta_title} />
                <meta property="og:description" content={singleproducts?.meta_description} />
                <meta property="og:image" content={singleproducts?.image} />
            </Helmet>

            <section className='product-details-section'>
                <Container>
                    <div className='product-details-content'>
                        <Row className="align-items-center">
                            {/* Main Image with Zoom Effect */}
                            <Col md={5} lg={5} xs={12} sm={11} className='text-end mb-4 mb-md-0'>
                                <div
                                    style={{
                                        position: 'relative',
                                        overflow: 'hidden',
                                        cursor: isZoomed ? 'zoom-in' : 'default'
                                    }}
                                    onMouseEnter={handleMouseEnter}
                                    onMouseLeave={handleMouseLeave}
                                    onMouseMove={handleMouseMove}
                                >
                                    <img
                                        src={`${baseUrl}/${selectedImage}`}
                                        alt={singleproducts?.title}
                                        className='img-fluid product-details-img'
                                        style={{
                                            display: 'block',
                                            width: '100%',
                                            transform: isZoomed ? 'scale(2)' : 'scale(1)',
                                            transformOrigin: transformOrigin,
                                            transition: 'transform 0.1s ease-out'
                                        }}
                                    />
                                </div>

                                {/* Images Slider/Gallery */}
                                {images?.length > 1 && (
                                    <div className="d-flex flex-wrap gap-2 mt-3 justify-content-center">
                                        {images.map((img, idx) => (
                                            <img
                                                key={idx}
                                                src={`${baseUrl}/${img}`}
                                                alt={`thumb-${idx}`}
                                                className={`img-thumbnail cursor-pointer ${selectedImage === img ? "border border-3 border-white shadow-lg" : ""}`}
                                                style={{
                                                    width: "70px",
                                                    height: "70px",
                                                    objectFit: "cover",
                                                    cursor: "pointer",
                                                }}
                                                onClick={() => setSelectedImage(img)}
                                            />
                                        ))}
                                    </div>
                                )}
                            </Col>

                            {/* Product Details */}
                            <Col md={6} lg={6} xs={12} sm={11}>
                                <h4 className='product-name'>{singleproducts?.title}</h4>
                                <p className='product-description' dangerouslySetInnerHTML={{ __html: singleproducts?.description }} />
                                <h6 className='products-tags'>Target Species: {singleproducts?.targetspecies}</h6>

                                <div className="d-flex flex-wrap gap-2 mt-3">
                                    <button className='btn btn-primary'>
                                        <a
                                            href={`${baseUrl}/${singleproducts?.technical_enquiry}`}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className='text-white text-decoration-none'
                                        >
                                            Technical Enquiry
                                        </a>
                                    </button>
                                    <button className='btn btn-secondary'>

                                        <a href={`${baseUrl}/${singleproducts?.pack_insert}`}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className='text-white text-decoration-none'
                                        >
                                            Pack Insert
                                        </a>
                                    </button>
                                    <WhatsappShareButton
                                        title={shareMessage}
                                        url={url}
                                        hashtag={shareHashtag}
                                    >
                                        <img src={buttonimg} alt='ask your vet' className='w-36' />
                                    </WhatsappShareButton>
                                </div>
                            </Col>
                        </Row>
                    </div >
                </Container >
            </section >

            {/* Accordion Section */}
            <section section className='accordian-section-product-details' >
                <Container>
                    <Row>
                        <Col md={12}>
                            <div className="accordion-container">
                                {sections.map((section, index) => (
                                    <Card key={index} className="mb-2 border-0 shadow-sm">
                                        <Card.Header
                                            className="d-flex justify-content-between align-items-center p-3 bg-light"
                                            onClick={() => toggleSection(index)}
                                            style={{ cursor: "pointer" }}
                                        >
                                            <span className="fw-bold">{section?.title}</span>
                                            <Button
                                                variant="link"
                                                className="text-dark p-0 border-0"
                                                style={{ marginLeft: "auto" }}
                                            >
                                                {openSection === index ? <Minus size={16} /> : <Plus size={16} />}
                                            </Button>
                                        </Card.Header>
                                        <Collapse in={openSection === index}>
                                            <Card.Body>
                                                <p dangerouslySetInnerHTML={{ __html: cleanHTML(section?.description) }} />
                                            </Card.Body>
                                        </Collapse>
                                    </Card>
                                ))}
                            </div>
                        </Col>
                    </Row>
                </Container>
            </section >

            {/* Related Products */}
            {
                products?.length > 0 && (
                    <>
                        <h2 className="text-center font-semibold py-2">Related Products</h2>
                        <RelatedProducts products={products} baseUrl={baseUrl} />
                    </>
                )
            }
        </>
    )
}

export default ProductDetails;